import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface MonitoringTag {
  id: string;
  user_id: string;
  name: string;
  category: 'lab' | 'symptom' | 'condition' | 'medication' | 'vital_sign';
  color: string;
  is_suggested: boolean;
  created_at: string;
}

export const DEFAULT_TAGS_BY_AREA: Record<string, Array<{ name: string, category: MonitoringTag['category'], color: string }>> = {
  'Medicina': [
    { name: 'Pressão Arterial', category: 'vital_sign', color: '#ef4444' },
    { name: 'Glicemia', category: 'lab', color: '#f59e0b' },
    { name: 'Hemograma', category: 'lab', color: '#3b82f6' },
    { name: 'Colesterol', category: 'lab', color: '#8b5cf6' },
    { name: 'Diabetes', category: 'condition', color: '#ec4899' },
    { name: 'Hipertensão', category: 'condition', color: '#ef4444' }
  ],
  'Nutrição': [
    { name: 'IMC', category: 'vital_sign', color: '#10b981' },
    { name: 'Peso', category: 'vital_sign', color: '#14b8a6' },
    { name: 'Glicemia', category: 'lab', color: '#f59e0b' },
    { name: 'Colesterol', category: 'lab', color: '#8b5cf6' },
    { name: 'Triglicerídeos', category: 'lab', color: '#6366f1' },
    { name: 'Vitamina D', category: 'lab', color: '#f97316' },
    { name: 'Vitamina B12', category: 'lab', color: '#0ea5e9' }
  ],
  'Enfermagem': [
    { name: 'Pressão Arterial', category: 'vital_sign', color: '#ef4444' },
    { name: 'Temperatura', category: 'vital_sign', color: '#f59e0b' },
    { name: 'Saturação O2', category: 'vital_sign', color: '#3b82f6' },
    { name: 'Frequência Cardíaca', category: 'vital_sign', color: '#ec4899' },
    { name: 'Glicemia', category: 'lab', color: '#f59e0b' },
    { name: 'Dor', category: 'symptom', color: '#ef4444' }
  ],
  'Psicologia': [
    { name: 'Ansiedade', category: 'symptom', color: '#f59e0b' },
    { name: 'Depressão', category: 'condition', color: '#6366f1' },
    { name: 'Insônia', category: 'symptom', color: '#8b5cf6' },
    { name: 'Estresse', category: 'symptom', color: '#ef4444' }
  ],
  'Fisioterapia': [
    { name: 'Dor', category: 'symptom', color: '#ef4444' },
    { name: 'Mobilidade', category: 'vital_sign', color: '#10b981' },
    { name: 'Força Muscular', category: 'vital_sign', color: '#3b82f6' },
    { name: 'Amplitude de Movimento', category: 'vital_sign', color: '#14b8a6' }
  ]
};

export function useMonitoringTags() {
  const [tags, setTags] = useState<MonitoringTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const { data, error } = await supabase
        .from('monitoring_tags')
        .select('*')
        .order('name');

      if (error) throw error;
      setTags((data || []) as MonitoringTag[]);
    } catch (error) {
      console.error('Error loading tags:', error);
      toast.error('Erro ao carregar tags');
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (tag: Omit<MonitoringTag, 'id' | 'user_id' | 'created_at'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('monitoring_tags')
        .insert({
          user_id: user.id,
          name: tag.name,
          category: tag.category,
          color: tag.color,
          is_suggested: tag.is_suggested
        })
        .select()
        .single();

      if (error) throw error;
      setTags(prev => [...prev, data as MonitoringTag]);
      toast.success('Tag criada com sucesso');
      return data as MonitoringTag;
    } catch (error: any) {
      console.error('Error creating tag:', error);
      if (error.message?.includes('duplicate')) {
        toast.error('Tag já existe');
      } else {
        toast.error('Erro ao criar tag');
      }
      throw error;
    }
  };

  const updateTag = async (id: string, updates: Partial<Pick<MonitoringTag, 'name' | 'category' | 'color'>>) => {
    try {
      const { data, error } = await supabase
        .from('monitoring_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setTags(prev => prev.map(t => t.id === id ? data as MonitoringTag : t));
      toast.success('Tag atualizada');
      return data as MonitoringTag;
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error('Erro ao atualizar tag');
      throw error;
    }
  };

  const deleteTag = async (id: string) => {
    try {
      const { error } = await supabase
        .from('monitoring_tags')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTags(prev => prev.filter(t => t.id !== id));
      toast.success('Tag excluída');
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Erro ao excluir tag');
      throw error;
    }
  };

  const getSuggestedTags = (healthArea: string) => {
    return DEFAULT_TAGS_BY_AREA[healthArea] || [];
  };

  const generateSuggestedTags = async (healthArea: string) => {
    setGenerating(true);
    try {
      const existingTagNames = tags.map(t => t.name);

      const { data, error } = await supabase.functions.invoke('suggest-monitoring-tags', {
        body: { healthArea, existingTags: existingTagNames }
      });

      if (error) throw error;

      if (!data?.tags || !Array.isArray(data.tags)) {
        throw new Error('Resposta inválida da API');
      }

      return data.tags;
    } catch (error: any) {
      console.error('Error generating tags:', error);
      toast.error(error.message || 'Erro ao gerar sugestões de tags');
      throw error;
    } finally {
      setGenerating(false);
    }
  };

  return {
    tags,
    loading,
    generating,
    createTag,
    updateTag,
    deleteTag,
    getSuggestedTags,
    generateSuggestedTags,
    reload: loadTags
  };
}
